import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiCallStarted } from "../middleware/api";
import moment from "moment";
import { forEach, includes } from "lodash";
import { ORDER_STATUS_VALUES, ROLES, USER_ROLES } from 'src/constants';
import OrderService from "src/_services/orderService";

export const editOrder = createAsyncThunk('orders/editOrder', async ({ role, id, orderDetails }, { rejectWithValue, dispatch }) => {
  // extract error handling to api class
  try {
    const response = await OrderService.updateOrder(role, id, orderDetails);
    return response.data.body;
  } catch (err) {
    return rejectWithValue(err.response.data);
  }
})

export const getZoneConnectionsCounts = createAsyncThunk('orders/getZoneConnectionsCounts',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      let response = await OrderService.getZonesAndConnectionsCountByStatus(payload)
      return response.data.body
    } catch (error) {
      return rejectWithValue(error.response?.data)
    }

  })


const slice = createSlice({
  name: "orders",
  initialState: {
    list: [],
    unassignedZones: [],
    lastOrderTime: null,
    orderDetails: {},
    orderDetailsError: null,
    orderDetailsSuccess: null,
    loading: false,
    lastFetch: null,
    rowCount: 0,
    pageSize: 0,
    page: 0,
    statusError: 0,
    zonesConnectionsOrdersCount: [],
    zonesConnectionsOrdersCountLoading: false,
    zoneConnectionsCountsError: 0
  },
  reducers: {
    ordersRequested: (orders, action) => {
      orders.loading = true;
    },
    ordersRequestSucceeded: (orders, action) => {
      const { docs, totalDocs, limit, page } = action.payload.body;
      if (page && page > orders.page) {
        orders.list = [...orders.list, ...docs];
        orders.page = page;
        orders.rowCount = totalDocs;
        orders.pageSize = limit;
      } else {
        // TODO: to be removed when pagination implemented in loadOrdersByTypeAndZone
        orders.list = docs;
        orders.page = 1;
        orders.rowCount = docs.length;
        orders.pageSize = docs.length;
      }
      orders.loading = false;
      orders.lastFetch = Date.now();
    },
    ordersRequestByStatusSucceeded: (orders, action) => {
      orders.unassignedZones = action.payload.body.zones;
      orders.lastOrderTime = action.payload.body.lastOrderTime
      orders.loading = false;
    },
    ordersRequestFailed: (orders, action) => {
      orders.loading = false;
      orders.statusError = action.status
    },
    orderDetailsRequestSucceeded: (orders, action) => {
      orders.orderDetails = action.payload.body;
    },
    orderAdded: (orders, action) => {
      orders.list.push(action.payload);
    },
    orderRemoved: (orders, action) => {
      const index = orders.list.findIndex(
        (order) => order.id === action.payload.id
      );
      if (index !== -1) {
        orders.list.splice(index, 1);
      }
    },
    orderEdited: (orders, action) => {
      const index = orders.list.findIndex(
        (order) => order.id === action.payload.id
      );
      if (index !== -1) {
        orders.list[index] = { ...action.payload }
      }
    },
    // orderSmsResent: (orders, action) => {
    //   forEach(orders.list, order => {
    //     if (includes(action.payload.ids, order.id)) {
    //       const dateStringUTC = moment().utc().format();
    //       order.locationSmsTime = dateStringUTC;
    //     }
    //   })
    // },
    pageChanged: (orders, action) => {
      const { page: targetPage } = action.payload;
      orders.page = targetPage;
    },
    clearOrders: (orders, action) => {
      orders.list = [];
      orders.page = 0;
      orders.rowCount = 0;
      orders.pageSize = 0;
      orders.unassignedZones = [];
      orders.lastOrderTime = null;
      orders.lastFetch = null;
      orders.loading = false;
      orders.statusError = 0
    },
    clearOrderDetails: (orders, action) => {
      orders.orderDetails = {};
      orders.orderDetailsError = null;
      orders.orderDetailsSuccess = null;
      orders.statusError = 0
    },
    clearOrderStatus: (orders, action) => {
      orders.orderDetailsError = null;
      orders.orderDetailsSuccess = null;
      orders.statusError = 0
    },
    clearZoneConnectionsCountsState: (state) => {
      state.zonesConnectionsOrdersCount = []
      state.zonesConnectionsOrdersCountLoading = false
      state.zoneConnectionsCountsError = 0
    },
  },
  extraReducers(builder) {
    builder
      .addCase(editOrder.pending, (state) => {
        state.orderDetailsSuccess = null;
        state.orderDetailsError = null;
        state.loading = true;
      })
      .addCase(editOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetailsSuccess = "Updated Successfully";
        state.orderDetails = action.payload;
        state.orderDetails.isComplete = true
      })
      .addCase(editOrder.rejected, (state, action) => {
        state.loading = false;
        state.orderDetailsError = action.payload.message;
      })
      .addCase(getZoneConnectionsCounts.pending, (state) => {
        state.zonesConnectionsOrdersCountLoading = true
        state.zoneConnectionsCountsError = 0
      })
      .addCase(getZoneConnectionsCounts.fulfilled, (state, action) => {
        state.zonesConnectionsOrdersCountLoading = false
        state.zonesConnectionsOrdersCount = action.payload
      })
      .addCase(getZoneConnectionsCounts.rejected, (state, action) => {
        state.zonesConnectionsOrdersCountLoading = false
        if (action.payload?.status === 400) {
          state.zoneConnectionsCountsError = 400
        }
      })
  }
});

export const {
  ordersRequested,
  ordersRequestSucceeded,
  ordersRequestByStatusSucceeded,
  ordersRequestByTypeAndZoneSucceeded,
  ordersRequestFailed,
  orderDetailsRequestSucceeded,
  orderAdded,
  orderRemoved,
  orderEdited,
  //orderSmsResent,
  pageChanged,
  clearOrders,
  clearOrderDetails,
  clearOrderStatus,
  clearZoneConnectionsCountsState
} = slice.actions;

// Action Creators
const commonEndpoint = `/v1/order`;
const apiEndpoints = {
  [ROLES.admin]: "/v1/admin-panel/order",
  [ROLES.merchant]: "/v1/merchant-dashboard/order",
  [ROLES.shippingCompany]: "/v1/merchant-dashboard/order",
  [USER_ROLES.MERCHANT]: "/v1/merchant-dashboard/order",
}

export const getOrdersBy_Id_OrderStatus = ({ page, status, id, orderType, role, type }) => (dispatch, getState) => {
  let url;
  if (role === ROLES.admin) {
    url = `${commonEndpoint}?page=${page}`
  } else {
    url = `${commonEndpoint}/${orderType}/${id}?status=${status}&page=${page}`
    if (!!type && !["null", "undefined"].includes(type)) {
      url += `&type=${type}`
    }
  }
  dispatch(
    apiCallStarted({
      url,
      onStart: ordersRequested.type,
      onSuccess: ordersRequestSucceeded.type,
      onError: ordersRequestFailed.type,
    })
  );
};

export const loadOrders = (page) => (dispatch, getState) => {
  let url = `${commonEndpoint}?page=${page}`
  dispatch(
    apiCallStarted({
      url,
      onStart: ordersRequested.type,
      onSuccess: ordersRequestSucceeded.type,
      onError: ordersRequestFailed.type,
    })
  );
};

export const loadOrdersByTypeAndZone = (role, page, type, zone) => (dispatch, getState) => {
  dispatch(
    apiCallStarted({
      url: `${apiEndpoints[role]}/zone/${zone}?type=${type}&page=${page}`,
      onStart: ordersRequested.type,
      onSuccess: ordersRequestSucceeded.type,
      onError: ordersRequestFailed.type,
    })
  );
};

export const loadOrdersByStatus = (role, type) => (dispatch, getState) => {
  dispatch(
    apiCallStarted({
      url: `${apiEndpoints[role]}/${type}`,
      onStart: ordersRequested.type,
      onSuccess: ordersRequestByStatusSucceeded.type,
      onError: ordersRequestFailed.type,
    })
  );
};

export const loadOrderDetails = (id, notificationId) => (dispatch, getState) => {
  let url = `${commonEndpoint}/${id}`;
  if (notificationId) {
    url += `?notification=${notificationId}`
  }
  dispatch(
    apiCallStarted({
      url,
      // onStart: ordersRequested.type,
      onSuccess: orderDetailsRequestSucceeded.type,
      onError: ordersRequestFailed.type,
    })
  );
};

export const loadOrdersByFilter = (payload) => (dispatch, getState) => {
  if (!!payload?.page) {
    payload.page = payload.page + ''
  }
  if (!!payload.limit) {
    payload.limit = payload.limit + ''
  }
  dispatch(
    apiCallStarted({
      url: `/v1/order/filter`,
      onStart: ordersRequested.type,
      onSuccess: ordersRequestSucceeded.type,
      onError: ordersRequestFailed.type,
      data: payload,
      method: 'post'
    })
  );
};

export default slice.reducer;
